/**
 * Copyright (c) 2025-2026 eBUCIS, LLC. All rights reserved.
 *
 * Coach Dashboard Component
 * Shows client management and progress for coach accounts
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import coachService from '../services/coachService'
import {
  Users,
  TrendingUp,
  BookOpen,
  UserPlus,
  Activity,
  Clock,
  MessageSquare,
  UserCheck,
  Mail,
  Building2,
  Check,
  X,
  Loader2,
  FileSignature,
  AlertCircle,
  Search
} from 'lucide-react'
import LoadingSpinner from './LoadingSpinner'
import ClientListRow from './ClientListRow'
import { filterAndSortClients, CLIENT_SORTS, DEFAULT_INACTIVE_DAYS } from '../utils/coachClientList'
import InviteClientModal from './InviteClientModal'
import TutoringAgreementModal from './tutoring/TutoringAgreementModal'
import { Avatar } from './profile'
import messagingService from '../services/messagingService'
import podMessagingService from '../services/podMessagingService'
import toast from 'react-hot-toast'
import { useRefetchOnFocus } from '../hooks/useRefetchOnFocus'
import CoachEventsSection from './calendar/CoachEventsSection'
import { useFeatureFlag } from '../hooks/useFeatureFlag'
import { createLogger } from '../utils/logger'

const logger = createLogger('CoachDashboard')

// Persisted client-list view preferences (search + sort), so a coach keeps their view across visits.
const CLIENT_LIST_PREFS_KEY = 'coach:clients:list'
const SORT_LABELS = {
  attention: 'Needs attention',
  recent: 'Last active',
  name: 'Name (A–Z)',
  score: 'Avg score'
}

const loadClientListPrefs = () => {
  try {
    return JSON.parse(localStorage.getItem(CLIENT_LIST_PREFS_KEY)) || {}
  } catch {
    return {}
  }
}

const CoachDashboard = () => {
  const { user, fetchCoachingNotificationCount } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const { enabled: coachCalendarEnabled } = useFeatureFlag('scheduling_calendar')
  const { enabled: tutorGroupsEnabled } = useFeatureFlag('tutor_groups')

  const [clients, setClients] = useState([])
  const [clientsLoading, setClientsLoading] = useState(true)
  const [pendingRequests, setPendingRequests] = useState([])
  const [pendingLoading, setPendingLoading] = useState(true)
  const [pendingAgreements, setPendingAgreements] = useState([])
  const [pendingAgreementsLoading, setPendingAgreementsLoading] = useState(true)
  const [signingAgreement, setSigningAgreement] = useState(null)
  const [receivedInvitations, setReceivedInvitations] = useState([])
  const [receivedLoading, setReceivedLoading] = useState(true)
  const [showInviteModal, setShowInviteModal] = useState(false)
  // relationshipId of the client whose tutoring agreement the coach is signing
  const [dashboardStats, setDashboardStats] = useState({
    totalClients: 0,
    pendingRequests: 0,
    activeToday: 0,
    maxClients: 50
  })
  const [coachStats, setCoachStats] = useState({
    totalChallenges: 0,
    averageProgress: 0
  })
  const [unreadMessages, setUnreadMessages] = useState(0)
  const [processingInvitation, setProcessingInvitation] = useState(null)

  // Compact client-list controls (persisted to localStorage). Default sort surfaces clients that need
  // attention (agreements to sign, then pending, then inactive) first.
  const [clientSearch, setClientSearch] = useState(() => loadClientListPrefs().search || '')
  const [clientSort, setClientSort] = useState(() => {
    const saved = loadClientListPrefs().sort
    return CLIENT_SORTS.includes(saved) ? saved : 'attention'
  })

  useEffect(() => {
    try {
      localStorage.setItem(
        CLIENT_LIST_PREFS_KEY,
        JSON.stringify({ search: clientSearch, sort: clientSort })
      )
    } catch {
      // ignore persistence failures (e.g. storage disabled)
    }
  }, [clientSearch, clientSort])

  // Clients shown in the list exclude pure 'pending' invites — those appear in the Sent/Received
  // invitation cards above so they are not double-listed here.
  const listableClients = useMemo(
    () => clients.filter((c) => c.relationshipStatus !== 'pending'),
    [clients]
  )
  const visibleClients = useMemo(
    () =>
      filterAndSortClients(listableClients, {
        search: clientSearch,
        sort: clientSort,
        inactiveDays: DEFAULT_INACTIVE_DAYS
      }),
    [listableClients, clientSearch, clientSort]
  )

  // Helper to get display name
  const getClientDisplayName = (client) => {
    if (client?.firstName || client?.lastName) {
      return `${client.firstName || ''} ${client.lastName || ''}`.trim()
    }
    return client?.publicName || client?.email || 'Unknown User'
  }

  // Track if component is mounted to prevent memory leaks from state updates after unmount
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true

    const loadData = async () => {
      // Use Promise.allSettled to prevent one failure from blocking others
      await Promise.allSettled([
        loadDashboardData(),
        loadPendingRequests(),
        loadPendingAgreements(),
        loadReceivedInvitations(),
        loadUnreadMessages()
      ])
    }

    loadData()

    // Clear location state after loading
    if (location.state?.freshLogin) {
      window.history.replaceState({}, document.title)
    }

    return () => {
      isMountedRef.current = false
    }
  }, [location.state?.freshLogin])

  // Refresh the client list + awaiting-agreements when the coach returns to the tab, so a relationship
  // that activated elsewhere (e.g. the guardian signed the consent) stops showing stale "pending"
  // without a manual reload.
  useRefetchOnFocus(() => {
    loadDashboardData()
    loadPendingAgreements()
  })

  const loadDashboardData = async () => {
    try {
      if (!isMountedRef.current) return
      setClientsLoading(true)

      // Fetch dashboard stats and clients in parallel
      const [statsResponse, clientsResponse] = await Promise.all([
        coachService.getStats(),
        coachService.getClients(true)
      ])

      // Check if still mounted before updating state
      if (!isMountedRef.current) return

      if (statsResponse.success) {
        setDashboardStats(statsResponse.stats)
      }

      const clientsData = clientsResponse.clients || []
      setClients(clientsData)

      // Calculate aggregate statistics - only for clients where coach has canViewProgress permission
      const clientsWithPermission = clientsData.filter(
        (client) => client.permissions?.canViewProgress !== false
      )

      const totalChallenges = clientsWithPermission.reduce(
        (sum, client) => sum + (client.stats?.totalChallenges || 0),
        0
      )

      const totalProgress = clientsWithPermission.reduce(
        (sum, client) => sum + (client.stats?.averageScore || 0),
        0
      )

      setCoachStats({
        totalChallenges,
        averageProgress:
          clientsWithPermission.length > 0
            ? Math.round((totalProgress / clientsWithPermission.length) * 100) / 100
            : 0
      })
    } catch (error) {
      logger.error('Error loading dashboard data:', error)
      if (isMountedRef.current) {
        toast.error('Failed to load dashboard data')
      }
    } finally {
      if (isMountedRef.current) {
        setClientsLoading(false)
      }
    }
  }

  const loadPendingRequests = async () => {
    try {
      if (!isMountedRef.current) return
      setPendingLoading(true)
      const response = await coachService.getPendingRequests()

      if (!isMountedRef.current) return
      if (response.success) {
        setPendingRequests(response.pendingRequests || [])
      }
    } catch (error) {
      logger.error('Error loading pending requests:', error)
      if (isMountedRef.current) {
        setPendingRequests([])
      }
    } finally {
      if (isMountedRef.current) {
        setPendingLoading(false)
      }
    }
  }

  const loadPendingAgreements = async () => {
    try {
      if (!isMountedRef.current) return
      setPendingAgreementsLoading(true)
      const response = await coachService.getPendingAgreements()

      if (!isMountedRef.current) return
      if (response.success) {
        setPendingAgreements(response.pendingAgreements || [])
      }
    } catch (error) {
      logger.error('Error loading pending agreements:', error)
      if (isMountedRef.current) {
        setPendingAgreements([])
      }
    } finally {
      if (isMountedRef.current) {
        setPendingAgreementsLoading(false)
      }
    }
  }

  const loadReceivedInvitations = async () => {
    try {
      if (!isMountedRef.current) return
      setReceivedLoading(true)
      const response = await coachService.getReceivedInvitations()

      if (!isMountedRef.current) return
      if (response.success) {
        setReceivedInvitations(response.invitations || [])
      }
    } catch (error) {
      logger.error('Error loading received invitations:', error)
      if (isMountedRef.current) {
        setReceivedInvitations([])
      }
    } finally {
      if (isMountedRef.current) {
        setReceivedLoading(false)
      }
    }
  }

  const loadUnreadMessages = async () => {
    try {
      if (!isMountedRef.current) return
      // The Messages button opens an inbox with both tabs, so the badge counts both systems:
      // relationship-anchored 1:1 conversations and pod threads. Pod threads resolve to 0 when the
      // feature is off for this account, so the direct count still shows.
      const [direct, podUnread] = await Promise.all([
        messagingService.getUnreadCount(),
        podMessagingService.getUnreadCount().catch(() => 0)
      ])
      if (!isMountedRef.current) return
      const directCount = direct?.success ? direct.unreadCount || 0 : 0
      setUnreadMessages(directCount + podUnread)
    } catch (error) {
      logger.error('Error loading unread messages:', error)
    }
  }

  const handleAcceptInvitation = async (relationshipId) => {
    setProcessingInvitation(relationshipId)
    try {
      const response = await coachService.acceptReceivedInvitation(relationshipId)
      if (response.success) {
        toast.success('Client added successfully!')
        loadReceivedInvitations()
        loadDashboardData()
        fetchCoachingNotificationCount() // Update sidebar badge
      }
    } catch (error) {
      logger.error('Error accepting invitation:', error)
      toast.error(error.response?.data?.message || 'Failed to accept invitation')
    } finally {
      setProcessingInvitation(null)
    }
  }

  const handleDeclineInvitation = async (relationshipId) => {
    setProcessingInvitation(relationshipId)
    try {
      const response = await coachService.declineReceivedInvitation(relationshipId)
      if (response.success) {
        toast.success('Request declined')
        loadReceivedInvitations()
        fetchCoachingNotificationCount() // Update sidebar badge
      }
    } catch (error) {
      logger.error('Error declining invitation:', error)
      toast.error(error.response?.data?.message || 'Failed to decline invitation')
    } finally {
      setProcessingInvitation(null)
    }
  }

  const handleCancelInvitation = async (relationshipId) => {
    try {
      const response = await coachService.cancelPendingRequest(relationshipId)
      if (response.success) {
        toast.success('Invitation cancelled')
        loadPendingRequests()
      }
    } catch (error) {
      logger.error('Error cancelling invitation:', error)
      toast.error(error.response?.data?.message || 'Failed to cancel invitation')
    }
  }

  const handleInviteClient = () => {
    setShowInviteModal(true)
  }

  const handleInviteSent = () => {
    // Refresh the dashboard data after invitation is sent
    loadDashboardData()
    loadPendingRequests()
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
                Welcome back, Coach{user?.firstName ? ` ${user.firstName}` : ''}!
              </h1>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Manage your clients and track their progress
              </p>
            </div>

            {/* Client Count Badge and Action Buttons */}
            <div className="flex items-center gap-3">
              <span className="px-3 py-1 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-sm font-medium">
                {dashboardStats.totalClients} / {dashboardStats.maxClients} clients
              </span>
              {tutorGroupsEnabled && (
                <button
                  onClick={() => navigate('/pod?promote=1')}
                  className="btn btn-secondary inline-flex items-center gap-2"
                  title="Team up with other tutors: promote your practice to a Tutoring Pod"
                >
                  <Users className="w-4 h-4" />
                  <span className="hidden sm:inline">Promote to Pod</span>
                </button>
              )}
              <button
                onClick={() => navigate('/messages')}
                className="btn btn-secondary inline-flex items-center gap-2 relative"
                title="Messages"
              >
                <MessageSquare className="w-4 h-4" />
                <span className="hidden sm:inline">Messages</span>
                {unreadMessages > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
                    {unreadMessages > 9 ? '9+' : unreadMessages}
                  </span>
                )}
              </button>
              <button
                onClick={handleInviteClient}
                disabled={dashboardStats.totalClients >= dashboardStats.maxClients}
                className="btn btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                title={
                  dashboardStats.totalClients >= dashboardStats.maxClients
                    ? 'Maximum clients reached'
                    : 'Invite a new client'
                }
              >
                <UserPlus className="w-4 h-4" />
                <span className="hidden sm:inline">Add Client</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Received Invitations - Users who want this coach */}
      {!receivedLoading && receivedInvitations.length > 0 && (
        <div className="card border-2 border-emerald-200 dark:border-emerald-800 bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20">
          <div className="card-body">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 bg-emerald-100 dark:bg-emerald-900/40 rounded-xl">
                <UserCheck className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  New Client Requests
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {receivedInvitations.length}{' '}
                  {receivedInvitations.length === 1 ? 'person wants' : 'people want'} you to be
                  their coach
                </p>
              </div>
              <span className="ml-auto px-3 py-1 text-sm font-semibold bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 rounded-full">
                {receivedInvitations.length} new
              </span>
            </div>

            <div className="space-y-3">
              {receivedInvitations.map((invitation) => (
                <div
                  key={invitation.id}
                  className="flex items-center gap-4 bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow"
                >
                  <Avatar
                    type={invitation.client?.avatarType || 'dicebear'}
                    seed={invitation.client?.avatarSeed}
                    style={invitation.client?.avatarStyle || 'bottts'}
                    name={getClientDisplayName(invitation.client)}
                    size="md"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 dark:text-gray-100">
                      {getClientDisplayName(invitation.client)}
                    </p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <Mail className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        {invitation.client?.email}
                      </span>
                    </div>
                    {invitation.organization && (
                      <div className="flex items-center gap-1.5 mt-1">
                        <Building2 className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          {invitation.organization.name}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleAcceptInvitation(invitation.id)}
                      disabled={processingInvitation === invitation.id}
                      className="btn btn-primary btn-sm inline-flex items-center gap-1.5"
                    >
                      {processingInvitation === invitation.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Check className="w-4 h-4" />
                      )}
                      Accept
                    </button>
                    <button
                      onClick={() => handleDeclineInvitation(invitation.id)}
                      disabled={processingInvitation === invitation.id}
                      className="btn btn-secondary btn-sm inline-flex items-center gap-1.5"
                    >
                      <X className="w-4 h-4" />
                      Decline
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Pending Requests - Invitations sent by coach */}
      {!pendingLoading && pendingRequests.length > 0 && (
        <div className="card border border-amber-200 dark:border-amber-800 bg-gradient-to-br from-amber-50/50 to-orange-50/50 dark:from-amber-900/10 dark:to-orange-900/10">
          <div className="card-body">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 bg-amber-100 dark:bg-amber-900/40 rounded-xl">
                <Clock className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Sent Invitations
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Waiting for {pendingRequests.length}{' '}
                  {pendingRequests.length === 1 ? 'user' : 'users'} to accept your invitation
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {pendingRequests.map((request) => (
                <div
                  key={request.id}
                  className="flex items-center gap-4 bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700"
                >
                  <Avatar
                    type={request.client?.avatarType || 'dicebear'}
                    seed={request.client?.avatarSeed}
                    style={request.client?.avatarStyle || 'bottts'}
                    name={getClientDisplayName(request.client)}
                    size="md"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 dark:text-gray-100">
                      {getClientDisplayName(request.client)}
                    </p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <Mail className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        {request.client?.email}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          request.status === 'pending_guardian_approval'
                            ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                            : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                        }`}
                      >
                        {request.status === 'pending_guardian_approval'
                          ? '🛡️ Guardian Review'
                          : '⏳ Awaiting Response'}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleCancelInvitation(request.id)}
                    className="btn btn-sm text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 dark:text-red-400 dark:hover:text-red-300 dark:bg-red-900/20 dark:hover:bg-red-900/30 border border-red-200 dark:border-red-800"
                    title="Cancel invitation"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Cancel
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Awaiting Agreements - accepted but not yet active (pending_agreements) */}
      {!pendingAgreementsLoading && pendingAgreements.length > 0 && (
        <div className="card border border-primary-200 dark:border-primary-800 bg-gradient-to-br from-primary-50/50 to-indigo-50/50 dark:from-primary-900/10 dark:to-indigo-900/10">
          <div className="card-body">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 bg-primary-100 dark:bg-primary-900/40 rounded-xl">
                <FileSignature className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Awaiting Agreements
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {pendingAgreements.length}{' '}
                  {pendingAgreements.length === 1 ? 'student has' : 'students have'} accepted but
                  tutoring can&apos;t begin until the required agreements are signed
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {pendingAgreements.map((pa) => {
                const missing = pa.readiness?.missing || []
                const coachMustSign = missing.some((m) => m.code === 'COACH_AGREEMENT')
                return (
                  <div
                    key={pa.relationshipId}
                    className="flex flex-col gap-3 bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 sm:flex-row sm:items-start sm:gap-4"
                  >
                    <Avatar
                      type={pa.avatarType || 'dicebear'}
                      seed={pa.avatarSeed}
                      style={pa.avatarStyle || 'bottts'}
                      name={getClientDisplayName(pa)}
                      size="md"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 dark:text-gray-100">
                        {getClientDisplayName(pa)}
                      </p>
                      <div className="flex items-center gap-1.5 mt-1">
                        <Mail className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-sm text-gray-500 dark:text-gray-400 truncate">
                          {pa.email}
                        </span>
                      </div>

                      {missing.length > 0 ? (
                        <div className="mt-3">
                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
                            Outstanding
                          </p>
                          <ul className="space-y-1.5">
                            {missing.map((item) => (
                              <li key={item.code} className="flex items-start gap-2 text-sm">
                                <AlertCircle className="w-4 h-4 text-primary-500 dark:text-primary-400 mt-0.5 flex-shrink-0" />
                                <span className="text-gray-700 dark:text-gray-300">
                                  {item.label}
                                  {item.party && (
                                    <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 capitalize">
                                      {item.party}
                                    </span>
                                  )}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : (
                        <div className="mt-3 flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
                          <AlertCircle className="w-4 h-4 flex-shrink-0" />
                          <span>Awaiting final activation — refresh shortly.</span>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col items-start gap-2 sm:items-end">
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 whitespace-nowrap">
                        📝 Agreements Pending
                      </span>
                      {coachMustSign && (
                        <button
                          onClick={() =>
                            setSigningAgreement({
                              relationshipId: pa.relationshipId,
                              studentName: getClientDisplayName(pa)
                            })
                          }
                          className="btn btn-sm btn-primary whitespace-nowrap"
                        >
                          <FileSignature className="w-4 h-4 mr-1" />
                          Sign coach agreement
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* Coach Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center">
                <Users className="w-4 h-4 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Active Clients
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {clientsLoading ? '...' : dashboardStats.totalClients}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-success-100 dark:bg-success-900/30 rounded-lg flex items-center justify-center">
                <BookOpen className="w-4 h-4 text-success-600 dark:text-success-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Total Challenges
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {clientsLoading ? '...' : coachStats.totalChallenges}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-warning-100 dark:bg-warning-900/30 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-warning-600 dark:text-warning-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Avg. Progress
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {clientsLoading
                    ? '...'
                    : coachStats.totalChallenges === 0
                      ? 'No data'
                      : `${coachStats.averageProgress}%`}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center">
                <Activity className="w-4 h-4 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Today</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {clientsLoading ? '...' : dashboardStats.activeToday}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Clients Overview — compact, searchable, sortable list (scales to ~50 clients) */}
      <div>
        <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Clients & Progress
            {!clientsLoading && listableClients.length > 0 && (
              <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                {visibleClients.length === listableClients.length
                  ? `(${listableClients.length})`
                  : `(${visibleClients.length} of ${listableClients.length})`}
              </span>
            )}
          </h3>

          {!clientsLoading && listableClients.length > 0 && (
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  placeholder="Search clients..."
                  aria-label="Search clients"
                  className="input pl-9 py-1.5 text-sm w-44 sm:w-56"
                />
              </div>
              <select
                value={clientSort}
                onChange={(e) => setClientSort(e.target.value)}
                aria-label="Sort clients"
                className="input py-1.5 text-sm w-auto"
              >
                {CLIENT_SORTS.map((key) => (
                  <option key={key} value={key}>
                    {SORT_LABELS[key]}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {clientsLoading ? (
          <div className="card">
            <div className="card-body flex items-center justify-center py-8">
              <LoadingSpinner size="sm" />
              <span className="ml-2 text-gray-600 dark:text-gray-400">Loading clients...</span>
            </div>
          </div>
        ) : listableClients.length === 0 ? (
          <div className="card">
            <div className="card-body text-center py-12">
              <Users className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                No clients yet
              </h4>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                Invite your first client to start coaching
              </p>
              <button
                onClick={handleInviteClient}
                className="btn btn-primary inline-flex items-center gap-2"
              >
                <UserPlus className="w-4 h-4" />
                Invite Client
              </button>
            </div>
          </div>
        ) : visibleClients.length === 0 ? (
          <div className="card">
            <div className="card-body text-center py-10">
              <Search className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No clients match &ldquo;{clientSearch}&rdquo;
              </p>
            </div>
          </div>
        ) : (
          <div className="card">
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {visibleClients.map((client) => (
                <ClientListRow
                  key={client.id}
                  client={client}
                  inactiveDays={DEFAULT_INACTIVE_DAYS}
                  onSignAgreement={(relationshipId) => setSigningAgreement({ relationshipId })}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Coach Events Section — behind scheduling_calendar feature flag */}
      {coachCalendarEnabled && <CoachEventsSection />}

      {/* Invite Client Modal */}
      <InviteClientModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        organizations={(user?.organizations || []).filter((org) =>
          ['admin', 'coach', 'staff'].includes(org.role)
        )}
        onInviteSent={handleInviteSent}
      />

      {/* Tutoring Agreement Modal (coach signs their agreement) */}
      <TutoringAgreementModal
        isOpen={!!signingAgreement}
        relationshipId={signingAgreement?.relationshipId}
        signerRole="coach"
        studentName={signingAgreement?.studentName}
        onClose={() => setSigningAgreement(null)}
        onSigned={() => {
          loadPendingAgreements()
          loadDashboardData()
        }}
      />
    </div>
  )
}

export default CoachDashboard
